import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';

const POLYGON_CHAIN_ID = 137;
const WALLETCONNECT_PROJECT_ID = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || '';
const WALLET_STORAGE_KEY = 'crypxa_wallet';

/** Params for wallet_addEthereumChain so MetaMask can add and switch to chains it doesn't have (e.g. zkSync Era). */
const CHAIN_PARAMS_FOR_ADD: Record<number, { chainId: string; chainName: string; nativeCurrency: { name: string; symbol: string; decimals: number }; rpcUrls: string[]; blockExplorerUrls: string[] }> = {
  1: { chainId: '0x1', chainName: 'Ethereum', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://eth.llamarpc.com'], blockExplorerUrls: ['https://etherscan.io'] },
  10: { chainId: '0xa', chainName: 'Optimism', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://mainnet.optimism.io'], blockExplorerUrls: ['https://optimism.etherscan.io'] },
  56: { chainId: '0x38', chainName: 'BNB Chain', nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 }, rpcUrls: ['https://bsc-dataseed.binance.org'], blockExplorerUrls: ['https://bscscan.com'] },
  100: { chainId: '0x64', chainName: 'Gnosis', nativeCurrency: { name: 'xDAI', symbol: 'XDAI', decimals: 18 }, rpcUrls: ['https://rpc.gnosischain.com'], blockExplorerUrls: ['https://gnosis.blockscout.com'] },
  137: { chainId: '0x89', chainName: 'Polygon', nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 }, rpcUrls: ['https://polygon-rpc.com'], blockExplorerUrls: ['https://polygonscan.com'] },
  324: { chainId: '0x144', chainName: 'zkSync Era Mainnet', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://mainnet.era.zksync.io'], blockExplorerUrls: ['https://explorer.zksync.io'] },
  42161: { chainId: '0xa4b1', chainName: 'Arbitrum One', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://arb1.arbitrum.io/rpc'], blockExplorerUrls: ['https://arbiscan.io'] },
  43114: { chainId: '0xa86a', chainName: 'Avalanche C-Chain', nativeCurrency: { name: 'AVAX', symbol: 'AVAX', decimals: 18 }, rpcUrls: ['https://api.avax.network/ext/bc/C/rpc'], blockExplorerUrls: ['https://snowtrace.io'] },
  5000: { chainId: '0x1388', chainName: 'Mantle', nativeCurrency: { name: 'MNT', symbol: 'MNT', decimals: 18 }, rpcUrls: ['https://rpc.mantle.xyz'], blockExplorerUrls: ['https://mantlescan.xyz'] },
  59144: { chainId: '0xe708', chainName: 'Linea', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://rpc.linea.build'], blockExplorerUrls: ['https://lineascan.build'] },
  81457: { chainId: '0x13e31', chainName: 'Blast', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://rpc.blast.io'], blockExplorerUrls: ['https://blastscan.io'] },
  8453: { chainId: '0x2105', chainName: 'Base', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://mainnet.base.org'], blockExplorerUrls: ['https://basescan.org'] },
  534352: { chainId: '0x82750', chainName: 'Scroll', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://rpc.scroll.io'], blockExplorerUrls: ['https://scrollscan.com'] },
};

function loadWalletStorage(): { type: 'injected' | 'walletconnect'; walletId?: string } | null {
  try {
    const raw = localStorage.getItem(WALLET_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as { type?: string; walletId?: string };
    if (data?.type === 'injected' || data?.type === 'walletconnect') return data as { type: 'injected' | 'walletconnect'; walletId?: string };
    return null;
  } catch {
    return null;
  }
}

function saveWalletStorage(type: 'injected' | 'walletconnect', walletId?: string) {
  try {
    localStorage.setItem(WALLET_STORAGE_KEY, JSON.stringify({ type, walletId: walletId ?? undefined }));
  } catch {
    // ignore
  }
}

function clearWalletStorage() {
  try {
    localStorage.removeItem(WALLET_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export type ConnectionType = 'injected' | 'walletconnect' | null;

type InjectedProvider = {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on?: (event: string, callback: (payload: unknown) => void) => void;
};

interface WalletContextType {
  account: string | null;
  chainId: number | null;
  connecting: boolean;
  connectionType: ConnectionType;
  isWrongChain: boolean;
  /** True when wallet is connected but on a different chain than the given one (for multi-chain swap). */
  isWrongChainFor: (selectedChainId: number) => boolean;
  connectWallet: () => Promise<void>;
  connectWithProvider: (provider: InjectedProvider, walletId?: string) => Promise<void>;
  connectWalletConnect: () => Promise<void>;
  disconnect: () => void;
  switchToPolygon: () => Promise<void>;
  /** Switch wallet to the given chain (e.g. for multi-chain swap). */
  switchToChain: (chainId: number) => Promise<void>;
  truncateAddress: (addr: string) => string;
  getProvider: () => unknown;
  wcUri: string | null;
  wcModalOpen: boolean;
  closeWcModal: () => void;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const WalletProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [account, setAccount] = useState<string | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connectionType, setConnectionType] = useState<ConnectionType>(null);
  const [wcUri, setWcUri] = useState<string | null>(null);
  const [wcModalOpen, setWcModalOpen] = useState(false);
  const wcProviderRef = useRef<{ request: (args: { method: string; params?: unknown[] }) => Promise<unknown>; disconnect?: () => Promise<void> } | null>(null);
  const injectedProviderRef = useRef<InjectedProvider | null>(null);
  const triedReconnectRef = useRef(false);

  const isWrongChain = chainId !== null && chainId !== POLYGON_CHAIN_ID;

  const connectWallet = useCallback(async () => {
    if (!window.ethereum) return;
    injectedProviderRef.current = window.ethereum as InjectedProvider;
    setConnecting(true);
    try {
      const accounts = (await window.ethereum.request({ method: 'eth_requestAccounts' })) as string[];
      const cid = (await window.ethereum.request({ method: 'eth_chainId' })) as string;
      setAccount(accounts[0] ?? null);
      setChainId(cid ? parseInt(cid, 16) : null);
      setConnectionType('injected');
      wcProviderRef.current = null;
      saveWalletStorage('injected');
    } catch {
      injectedProviderRef.current = null;
      setAccount(null);
      setConnectionType(null);
    } finally {
      setConnecting(false);
    }
  }, []);

  const connectWithProvider = useCallback(async (provider: InjectedProvider) => {
    setConnecting(true);
    injectedProviderRef.current = provider;
    try {
      const accounts = (await provider.request({ method: 'eth_requestAccounts' })) as string[];
      const cid = (await provider.request({ method: 'eth_chainId' })) as string;
      setAccount(accounts[0] ?? null);
      setChainId(cid ? parseInt(String(cid), 16) : null);
      setConnectionType('injected');
      wcProviderRef.current = null;
      saveWalletStorage('injected');
    } catch (err) {
      injectedProviderRef.current = null;
      setAccount(null);
      setConnectionType(null);
      throw err;
    } finally {
      setConnecting(false);
    }
  }, []);

  const connectWalletConnect = useCallback(async () => {
    if (!WALLETCONNECT_PROJECT_ID) {
      console.warn('VITE_WALLETCONNECT_PROJECT_ID not set. Get one at https://cloud.walletconnect.com');
      return;
    }
    setConnecting(true);
    setWcUri(null);
    setWcModalOpen(true);
    try {
      const { EthereumProvider } = await import('@walletconnect/ethereum-provider');
      const provider = await EthereumProvider.init({
        projectId: WALLETCONNECT_PROJECT_ID,
        chains: [POLYGON_CHAIN_ID],
        showQrModal: false,
      });
      provider.on('display_uri', (uri: string) => setWcUri(uri));
      provider.on('disconnect', () => {
        wcProviderRef.current = null;
        setAccount(null);
        setChainId(null);
        setConnectionType(null);
        setWcModalOpen(false);
      });
      await provider.connect();
      const accounts = (await provider.request({ method: 'eth_accounts' })) as string[];
      const cid = (await provider.request({ method: 'eth_chainId' })) as string;
      wcProviderRef.current = provider as unknown as { request: (args: { method: string; params?: unknown[] }) => Promise<unknown>; disconnect?: () => Promise<void> };
      setAccount(accounts[0] ?? null);
      setChainId(cid ? parseInt(String(cid), 16) : null);
      setConnectionType('walletconnect');
      setWcModalOpen(false);
      setWcUri(null);
      saveWalletStorage('walletconnect');
    } catch (e) {
      if (String(e).toLowerCase().includes('user rejected') === false) {
        console.error('WalletConnect error:', e);
      }
      setAccount(null);
      setConnectionType(null);
      setWcModalOpen(false);
      setWcUri(null);
    } finally {
      setConnecting(false);
    }
  }, []);

  const closeWcModal = useCallback(() => {
    setWcModalOpen(false);
    setWcUri(null);
    if (connecting) setConnecting(false);
  }, [connecting]);

  const disconnect = useCallback(async () => {
    if (connectionType === 'walletconnect' && wcProviderRef.current?.disconnect) {
      try {
        await wcProviderRef.current.disconnect();
      } catch {
        // ignore
      }
      wcProviderRef.current = null;
    }
    injectedProviderRef.current = null;
    setAccount(null);
    setChainId(null);
    setConnectionType(null);
    setWcModalOpen(false);
    setWcUri(null);
    clearWalletStorage();
  }, [connectionType]);

  const switchToChain = useCallback(async (targetChainId: number) => {
    const provider = connectionType === 'walletconnect' ? wcProviderRef.current : (injectedProviderRef.current ?? (typeof window !== 'undefined' ? (window as unknown as { ethereum?: unknown }).ethereum : undefined));
    if (!provider) return;
    const req = (provider as { request: (args: { method: string; params?: unknown[] }) => Promise<unknown> }).request.bind(provider);
    const hexChainId = `0x${targetChainId.toString(16)}`;
    try {
      await req({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexChainId }],
      });
      setChainId(targetChainId);
    } catch (err: unknown) {
      const code = (err as { code?: number })?.code;
      const message = String((err as { message?: string })?.message ?? '');
      // MetaMask 4902: chain not in wallet – add it then switch
      if (code === 4902 || message.includes('Unrecognized chain ID') || message.includes('wallet_addEthereumChain')) {
        const params = CHAIN_PARAMS_FOR_ADD[targetChainId];
        if (params) {
          try {
            await req({
              method: 'wallet_addEthereumChain',
              params: [{ chainId: params.chainId, chainName: params.chainName, nativeCurrency: params.nativeCurrency, rpcUrls: params.rpcUrls, blockExplorerUrls: params.blockExplorerUrls }],
            });
            await req({ method: 'wallet_switchEthereumChain', params: [{ chainId: hexChainId }] });
            setChainId(targetChainId);
          } catch {
            // User rejected add or switch
          }
        }
      }
    }
  }, [connectionType]);

  const switchToPolygon = useCallback(() => switchToChain(POLYGON_CHAIN_ID), [switchToChain]);

  const isWrongChainFor = useCallback(
    (selectedChainId: number) => chainId != null && chainId !== selectedChainId,
    [chainId]
  );

  const getProvider = useCallback(() => {
    if (connectionType === 'walletconnect' && wcProviderRef.current) return wcProviderRef.current;
    return injectedProviderRef.current ?? window.ethereum ?? undefined;
  }, [connectionType]);

  // On mount: restore wallet connection after hard refresh (no prompt) – stays until user disconnects
  const tryRestoreInjected = useCallback(() => {
    const stored = loadWalletStorage();
    if (stored?.type !== 'injected') return false;
    const provider = (typeof window !== 'undefined' ? (window as unknown as { ethereum?: InjectedProvider }).ethereum : undefined) as InjectedProvider | undefined;
    if (!provider?.request) return false;
    provider
      .request({ method: 'eth_accounts' })
      .then((accounts: unknown) => {
        const list = accounts as string[];
        if (list?.length > 0) {
          injectedProviderRef.current = provider;
          setAccount(list[0]);
          setConnectionType('injected');
          provider.request({ method: 'eth_chainId' }).then((cid: unknown) => {
            setChainId(cid ? parseInt(String(cid), 16) : null);
          });
        }
      })
      .catch(() => {});
    return true;
  }, []);

  useEffect(() => {
    if (triedReconnectRef.current) return;
    triedReconnectRef.current = true;
    const stored = loadWalletStorage();
    if (!stored) return;

    if (stored.type === 'injected') {
      if (tryRestoreInjected()) return;
      // Extension may not be ready yet (e.g. after hard refresh) – retry after short delays
      const t1 = window.setTimeout(() => { if (!injectedProviderRef.current) tryRestoreInjected(); }, 400);
      const t2 = window.setTimeout(() => { if (!injectedProviderRef.current) tryRestoreInjected(); }, 1200);
      return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
    }

    if (stored.type === 'walletconnect' && WALLETCONNECT_PROJECT_ID) {
      (async () => {
        try {
          const { EthereumProvider } = await import('@walletconnect/ethereum-provider');
          const provider = await EthereumProvider.init({
            projectId: WALLETCONNECT_PROJECT_ID,
            chains: [POLYGON_CHAIN_ID],
            showQrModal: false,
          });
          const accounts = (await provider.request({ method: 'eth_accounts' })) as string[];
          if (accounts?.length > 0) {
            const cid = (await provider.request({ method: 'eth_chainId' })) as string;
            wcProviderRef.current = provider as unknown as { request: (args: { method: string; params?: unknown[] }) => Promise<unknown>; disconnect?: () => Promise<void> };
            setAccount(accounts[0]);
            setChainId(cid ? parseInt(cid, 16) : null);
            setConnectionType('walletconnect');
          } else clearWalletStorage();
          provider.on?.('disconnect', () => {
            wcProviderRef.current = null;
            setAccount(null);
            setChainId(null);
            setConnectionType(null);
            clearWalletStorage();
          });
        } catch {
          clearWalletStorage();
        }
      })();
    }
  }, [tryRestoreInjected]);

  useEffect(() => {
    const provider = injectedProviderRef.current ?? window.ethereum;
    if (connectionType === 'injected' && provider) {
      const onAccounts = (payload: unknown) => {
        const accounts = payload as string[];
        setAccount(accounts?.[0] ?? null);
      };
      const onChain = (payload: unknown) => {
        const hex = payload as string;
        setChainId(hex ? parseInt(hex, 16) : null);
      };
      provider.request({ method: 'eth_accounts' }).then((accounts: unknown) => {
        const a = accounts as string[];
        setAccount(a?.[0] ?? null);
      });
      provider.request({ method: 'eth_chainId' }).then((cid: unknown) => {
        setChainId(cid ? parseInt(String(cid), 16) : null);
      });
      provider.on?.('accountsChanged', onAccounts);
      provider.on?.('chainChanged', onChain);
    }
  }, [connectionType]);

  const truncateAddress = useCallback((addr: string) => (addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : ''), []);

  const value: WalletContextType = {
    account,
    chainId,
    connecting,
    connectionType,
    isWrongChain,
    isWrongChainFor,
    connectWallet,
    connectWithProvider,
    connectWalletConnect,
    disconnect,
    switchToPolygon,
    switchToChain,
    truncateAddress,
    getProvider,
    wcUri,
    wcModalOpen,
    closeWcModal,
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

export function useWallet(): WalletContextType {
  const ctx = useContext(WalletContext);
  if (ctx === undefined) throw new Error('useWallet must be used within WalletProvider');
  return ctx;
}

export function useWalletOptional(): WalletContextType | undefined {
  return useContext(WalletContext);
}
